import PropTypes from "prop-types";
import { useId } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

// third-party
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

// project import
import IconButton from "../../../components/@extended/IconButton";

// ==============================|| NOTIFICACOES - CONFIRMAR MARCAR TODAS ||============================== //
//
// Compartilhado pela pagina de listagem e pelo sino do cabecalho: os dois pedem
// a mesma confirmacao antes de `marcarTodasComoLidas`. O efeito e permanente (o
// PUT nao desmarca), por isso a confirmacao. O cabecalho segue o dialogo de
// alerta do app (nada e destruido, mas nao ha desfazer).
//
// Contrato:
// - `quantidade`: quantas notificacoes serao marcadas (o chamador congela os ids
//   ao abrir e passa o tamanho da lista; ver ListaNotificacoes).
// - `filtrosAtivos`: so a pagina usa; avisa que a acao inclui as ocultas pelo
//   filtro. O sino nao tem filtro e omite a prop.
// - `onConfirm` / `onClose`: o dialogo nao fecha sozinho; quem usa decide.

const descreverQuantidadeNaoLidas = (quantidade) =>
  quantidade === 1
    ? "Marcar a notificação não lida como lida?"
    : `Marcar as ${quantidade} notificações não lidas como lidas?`;

// Texto do snackbar de sucesso do "marcar todas" (compartilhado pelos dois
// chamadores para a mensagem ser a mesma).
export const mensagemSucessoEmLote = (quantidade) =>
  quantidade === 1
    ? "1 notificação marcada como lida."
    : `${quantidade} notificações marcadas como lidas.`;

function ConfirmarMarcarTodasDialog({
  open,
  quantidade,
  filtrosAtivos = false,
  onConfirm,
  onClose,
}) {
  const isDarkMode = useTheme().palette.mode === "dark";
  const tituloId = useId();
  const descricaoId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={tituloId}
      aria-describedby={descricaoId}
      PaperProps={{
        sx: { width: "547px", maxWidth: "calc(100% - 32px)" },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          background: "#F69B50",
          minHeight: "42px",
          borderRadius: "4px 4px 0px 0px",
          display: "flex",
          alignItems: "center",
          padding: "10px",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            component="span"
            sx={{ color: "#FFFFFF", fontSize: "16px", display: "flex" }}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </Box>
          <Typography
            id={tituloId}
            variant="body1"
            component="h2"
            sx={{
              fontFamily: '"Open Sans", Helvetica, sans-serif',
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: "21px",
              color: "#FFFFFF",
            }}
          >
            Marcar todas como lidas
          </Typography>
        </Stack>
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          sx={{
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "transparent",
              boxShadow: "none",
              color: "#FFFFFF",
            },
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>
      <DialogContent id={descricaoId}>
        <Typography
          component="div"
          sx={{ fontWeight: "bold", marginTop: "28px", color: "text.primary" }}
        >
          {descreverQuantidadeNaoLidas(quantidade)}
        </Typography>
        {filtrosAtivos && (
          <Typography
            component="div"
            sx={{ marginTop: "12px", color: "text.secondary" }}
          >
            Isso inclui as notificações ocultas pelos filtros.
          </Typography>
        )}
        <Typography
          component="div"
          sx={{ marginTop: "12px", color: "text.secondary" }}
        >
          Esta ação não pode ser desfeita.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ padding: "8px 24px 20px" }}>
        <Button
          onClick={onClose}
          autoFocus
          sx={{
            height: "32px",
            padding: "8px 16px",
            borderRadius: "4px",
            border: `1px solid ${
              isDarkMode ? "rgba(255, 255, 255, 0.40)" : "rgba(0, 0, 0, 0.40)"
            }`,
            background: isDarkMode ? "transparent" : "#FFF",
            fontSize: "13px",
            fontWeight: 600,
            color: isDarkMode
              ? "rgba(255, 255, 255, 0.87)"
              : "rgba(0, 0, 0, 0.60)",
            "&:hover": {
              background: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#F4F4F4",
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          sx={{
            height: "32px",
            padding: "8px 16px",
            borderRadius: "4px",
            background: "#F69B50",
            fontSize: "13px",
            fontWeight: 600,
            color: "#FFFFFF",
            textTransform: "none",
            "&:hover": { background: "#E58A3F" },
          }}
        >
          Sim, marcar como lidas
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ConfirmarMarcarTodasDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  quantidade: PropTypes.number.isRequired,
  filtrosAtivos: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ConfirmarMarcarTodasDialog;
